import { useMemo } from 'react'

import { useAppSelector } from 'hooks/reduxHooks'
import type { LuxPlusPricingContext } from 'components/OfferPage/offerPageUtils/propertyLoyaltyPackageUpgrade'
import { getCanRedeemLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { isGhaPriceDisplay } from 'selectors/routerSelectors'

/**
 * Reads the LuxPlus+ flags once so callers can price any number of rates with
 * `getPropertyRatePayablePrice`. `useLuxPlusDisplayedMode` covers the single-rate case; this exists
 * for surfaces pricing a variable-length list, where a hook per rate isn't possible.
 *
 * Uses `getCanRedeemLuxPlusBenefits` rather than the display hook's `getCanViewLuxPlusBenefits`,
 * because these surfaces quote the price that will be charged.
 */
export default function useLuxPlusPricingContext(): LuxPlusPricingContext {
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const isGhaPricing = useAppSelector(isGhaPriceDisplay)

  return useMemo(
    () => ({ luxPlusEnabled, canRedeemLuxPlusBenefits, isGhaPricing }),
    [luxPlusEnabled, canRedeemLuxPlusBenefits, isGhaPricing],
  )
}
